import React, { useState } from 'react';
import { runStripeSync, runStripeSyncFrom } from '../services/stripeSyncApi';
import { useAuth } from '../contexts/AuthContext'; 
import { useSuperAdmin } from '../contexts/SuperAdminContext';
import {
  RefreshCw,
  Play,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle
} from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const StripeSyncMonitor = () => {
  const { isSuperAdmin } = useSuperAdmin();

  const [sinceInput, setSinceInput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [isRunningFrom, setIsRunningFrom] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [lastResultSource, setLastResultSource] = useState(null); // 'cursor' | 'from'

  if (!isSuperAdmin) {
    return null;
  }

  const handleRunNow = async () => {
    try {
      setIsRunning(true);
      const result = await runStripeSync();
      setLastResult(result);
      setLastResultSource('cursor');
      toast.success(
        `Sync complete — ${result.processed}/${result.eventsSwept} events processed` +
        (result.failed > 0 ? `, ${result.failed} failed` : '')
      );
    } catch (error) {
      console.error('Error running sync:', error);
      toast.error(error.message || 'Failed to run Stripe sync');
    } finally {
      setIsRunning(false);
    }
  };

  const handleRunFrom = async () => {
    if (!sinceInput) {
      toast.error('Select a start date/time first');
      return;
    }

    // datetime-local gives "YYYY-MM-DDTHH:mm" — pad seconds for the
    // backend's ISO.DATE_TIME parser
    const since = sinceInput.length === 16 ? `${sinceInput}:00` : sinceInput;

    if (!window.confirm(
      `Sweep Stripe events from ${since} onward? This may reprocess a large window and can take a while.`
    )) {
      return;
    }

    try {
      setIsRunningFrom(true);
      const result = await runStripeSyncFrom(since);
      setLastResult(result);
      setLastResultSource('from');
      toast.success(
        `Sync complete — ${result.processed}/${result.eventsSwept} events processed` +
        (result.failed > 0 ? `, ${result.failed} failed` : '')
      );
    } catch (error) {
      console.error('Error running sync from timestamp:', error);
      toast.error(error.message || 'Failed to run Stripe sync from timestamp');
    } finally {
      setIsRunningFrom(false);
    }
  };

  return (
    <div className="bg-gray-900 rounded-xl shadow-lg">
      <ToastContainer position="top-right" theme="dark" />

      <div className="p-6 border-b border-gray-800">
        <h2 className="text-xl font-semibold text-gray-100 flex items-center gap-2">
          <RefreshCw className="h-5 w-5 text-blue-400" />
          Stripe Event Sync
        </h2>
        <p className="text-sm text-gray-400 mt-1">
          Reconcile local billing state against Stripe's event log. Use this after an
          outage to catch webhook events that were missed while the app was down.
        </p>
      </div>

      <div className="p-6 border-b border-gray-800">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Run from stored cursor */}
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-100">Run Sync Now</h3>
              <button
                onClick={handleRunNow}
                className="p-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isRunning || isRunningFrom}
                title="Sync from the last successful checkpoint"
              >
                <Play className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-gray-400">
              Sweeps events since the last successful sync checkpoint (same window the
              scheduled job uses). Use this for routine catch-up.
            </p>
            {isRunning && (
              <div className="text-sm text-yellow-400 mt-3 flex items-center gap-2">
                <Clock className="h-4 w-4 animate-spin" />
                Running...
              </div>
            )}
          </div>

          {/* Run from explicit timestamp */}
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-100">Run From Outage Start</h3>
              <button
                onClick={handleRunFrom}
                className="p-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isRunning || isRunningFrom || !sinceInput}
                title="Sweep from an explicit point in time"
              >
                <Play className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-gray-400 block">
                Since (outage start)
              </label>
              <input
                type="datetime-local"
                value={sinceInput}
                onChange={(e) => setSinceInput(e.target.value)}
                className="w-full px-2 py-1 bg-gray-700 rounded text-gray-100 text-sm"
              />
              <p className="text-xs text-gray-500">
                Interpreted as UTC by the server — adjust for your timezone if needed.
              </p>
            </div>
            {isRunningFrom && (
              <div className="text-sm text-yellow-400 mt-3 flex items-center gap-2">
                <Clock className="h-4 w-4 animate-spin" />
                Running — this may take a while for a large window...
              </div>
            )}
          </div>
        </div>
      </div>

      {lastResult && (
        <div className="p-6">
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-md font-medium text-gray-100 flex items-center gap-2">
                {lastResult.failed > 0 ? (
                  <AlertTriangle className="h-4 w-4 text-yellow-400" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 text-green-400" />
                )}
                Last Run Result
                <span className="text-xs text-gray-500 font-normal">
                  ({lastResultSource === 'from' ? 'from explicit timestamp' : 'from stored checkpoint'})
                </span>
              </h3>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-semibold text-gray-100">{lastResult.eventsSwept}</div>
                <div className="text-xs text-gray-400 uppercase tracking-wider">Events Swept</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-green-400">{lastResult.processed}</div>
                <div className="text-xs text-gray-400 uppercase tracking-wider">Processed</div>
              </div>
              <div>
                <div className={`text-2xl font-semibold ${lastResult.failed > 0 ? 'text-red-400' : 'text-gray-100'}`}>
                  {lastResult.failed}
                </div>
                <div className="text-xs text-gray-400 uppercase tracking-wider">Failed</div>
              </div>
            </div>
            {lastResult.failed > 0 && (
              <div className="mt-3 flex items-center gap-2 text-sm text-red-400">
                <XCircle className="h-4 w-4" />
                Some events failed to process — check server logs for details.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StripeSyncMonitor;